import { ApolloError } from '@apollo/client';

import { describeError, getRateLimitRejection } from '../../../common/rateLimit';
import { count } from '../manage/manage';

/**
 * Email campaigns (plan 4.4, API card 17.5) as plain functions. The merge-field
 * and readiness rules mirror `unknownMergeFields`, `renderMerge` and
 * `campaignProblems` in the API's src/crm/crm-email.ts: change both together.
 */

export type CampaignStatus = 'DRAFT' | 'SCHEDULED' | 'SENDING' | 'SENT' | 'CANCELED';
export type SendStatus = 'QUEUED' | 'SENT' | 'FAILED' | 'SKIPPED';

export interface Campaign {
  id: number;
  source: string;
  name: string;
  subject: string | null;
  preheader: string | null;
  fromName: string | null;
  fromEmail: string | null;
  bodyHtml: string | null;
  bodyText: string | null;
  status: CampaignStatus;
  segmentId: number | null;
  segmentName: string | null;
  scheduledAt: string | null;
  startedAt: string | null;
  sentAt: string | null;
  canceledAt: string | null;
  recipientCount: number | null;
  sentCount: number;
  openCount: number;
  machineOpenCount: number;
  clickCount: number;
  bounceCount: number;
  unsubCount: number;
  spamCount: number;
  openRate: number | null;
  clickRate: number | null;
  workflowStepCount: number;
  nbCreatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export const LIMITS = { name: 200, subject: 300, preheader: 300, fromName: 100, email: 320, testRecipients: 5 } as const;

export const STATUS_META: Record<CampaignStatus, { label: string; tone: string }> = {
  DRAFT: { label: 'Draft', tone: '#64748b' },
  SCHEDULED: { label: 'Scheduled', tone: '#4f46e5' },
  SENDING: { label: 'Sending', tone: '#0891b2' },
  SENT: { label: 'Sent', tone: '#059669' },
  CANCELED: { label: 'Cancelled', tone: '#dc2626' },
};

export const SEND_META: Record<SendStatus, { label: string; tone: string }> = {
  QUEUED: { label: 'Queued', tone: '#64748b' },
  SENT: { label: 'Sent', tone: '#059669' },
  FAILED: { label: 'Failed', tone: '#dc2626' },
  SKIPPED: { label: 'Skipped', tone: '#d97706' },
};

export const isMigrated = (c: Pick<Campaign, 'source'>) => c.source === 'NATIONBUILDER';

/** A draft of ours opens in the composer; anything else opens as a report. */
export const isEditable = (c: Pick<Campaign, 'source' | 'status'>) => !isMigrated(c) && c.status === 'DRAFT';

/** Deletable: a draft, or a cancelled campaign that never sent (the API's rule). */
export const isDeletable = (c: Pick<Campaign, 'source' | 'status' | 'sentCount'>) =>
  !isMigrated(c) && (c.status === 'DRAFT' || (c.status === 'CANCELED' && c.sentCount === 0));

/** 0.4123 → "41.2%"; nothing sent → "—". */
export const formatRate = (rate: number | null | undefined) => (rate == null ? '—' : `${(rate * 100).toFixed(rate >= 0.1 ? 0 : 1)}%`);

/* ------------------------------------------------------------------ *
 * Merge fields
 * ------------------------------------------------------------------ */

export const MERGE_FIELDS = ['first_name', 'last_name', 'full_name', 'name', 'first_name_or_friend', 'email', 'unsubscribe_url', 'unsubscribe_link'] as const;
type MergeField = (typeof MERGE_FIELDS)[number];

/** What the composer offers to insert, in NationBuilder's names. */
export const INSERTABLE: Array<{ tag: string; label: string }> = [
  { tag: '{{ recipient.first_name_or_friend }}', label: 'First name, or “Friend”' },
  { tag: '{{ recipient.first_name | default: "there" }}', label: 'First name, or a word of yours' },
  { tag: '{{ recipient.first_name }}', label: 'First name' },
  { tag: '{{ recipient.last_name }}', label: 'Last name' },
  { tag: '{{ recipient.full_name }}', label: 'Full name' },
  { tag: '{{ recipient.email }}', label: 'Their email address' },
  { tag: '{{ unsubscribe_link }}', label: 'An “Unsubscribe” link' },
];

const OUTPUT_TAG = /\{\{\s*([^{}]*?)\s*\}\}/g;
const LOGIC_TAG = /\{%[\s\S]*?%\}/g;
const DEFAULT_FILTER = /^\|\s*default\s*:\s*(?:"([^"]*)"|'([^']*)')\s*$/;

const parseTag = (inner: string): { field?: MergeField; fallback?: string } => {
  const bar = inner.indexOf('|');
  const name = (bar < 0 ? inner : inner.slice(0, bar)).trim().replace(/^recipient\./, '');
  const filter = bar < 0 ? undefined : inner.slice(bar).trim();
  const field = (MERGE_FIELDS as readonly string[]).includes(name) ? (name as MergeField) : undefined;
  if (!filter) return { field };
  const m = DEFAULT_FILTER.exec(filter);
  return m ? { field, fallback: m[1] ?? m[2] } : {};
};

const matchesOf = (re: RegExp, text: string): Array<RegExpExecArray> => {
  const out: Array<RegExpExecArray> = [];
  const g = new RegExp(re.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = g.exec(text))) out.push(m);
  return out;
};

/** Every merge tag that would not render, as written: what must be fixed before it can go. */
export const unknownMergeFields = (...texts: Array<string | null | undefined>): Array<string> => {
  const out: Array<string> = [];
  const add = (tag: string) => {
    if (!out.includes(tag)) out.push(tag);
  };
  texts.forEach((t) => {
    if (!t) return;
    matchesOf(OUTPUT_TAG, t).forEach((m) => {
      if (!parseTag(m[1]).field) add(m[0]);
    });
    matchesOf(LOGIC_TAG, t).forEach((m) => add(m[0].length > 60 ? `${m[0].slice(0, 57)}...` : m[0]));
  });
  return out;
};

export const hasUnsubscribeTag = (text: string | null | undefined) =>
  !!text && matchesOf(OUTPUT_TAG, text).some((m) => ['unsubscribe_url', 'unsubscribe_link'].includes(parseTag(m[1]).field ?? ''));

const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch] as string);

export interface SampleRecipient {
  firstName: string;
  lastName: string;
  email: string;
}

export const SAMPLE: SampleRecipient = { firstName: 'Ama', lastName: 'Owusu', email: 'ama@example.org' };

/** The merge fields filled as the sender fills them, for the preview. Unknown tags stay as written. */
export const renderMerge = (template: string, r: SampleRecipient, html: boolean): string => {
  const full = [r.firstName, r.lastName].filter(Boolean).join(' ');
  const values: Record<MergeField, string> = {
    first_name: r.firstName,
    last_name: r.lastName,
    full_name: full,
    name: full,
    first_name_or_friend: r.firstName || 'Friend',
    email: r.email,
    unsubscribe_url: '#unsubscribe',
    unsubscribe_link: '#unsubscribe',
  };
  return template.replace(new RegExp(OUTPUT_TAG.source, 'g'), (whole: string, inner: string) => {
    const tag = parseTag(inner);
    if (!tag.field) return whole;
    const value = values[tag.field] || tag.fallback || '';
    if (!html) return value;
    if (tag.field === 'unsubscribe_link') return `<a href="#unsubscribe">Unsubscribe</a>`;
    return escapeHtml(value);
  });
};

/**
 * The email as one person receives it, for the preview frame: the body with
 * their merge fields, then the footer the sender adds (postal address, and an
 * unsubscribe link unless the body has its own). Scripts never run: the frame
 * that shows it is sandboxed.
 */
export const previewDocument = (bodyHtml: string | null | undefined, r: SampleRecipient = SAMPLE): string => {
  const body = bodyHtml?.trim() ? renderMerge(bodyHtml, r, true) : '<p style="color:#94a3b8">The email’s body appears here as you write it.</p>';
  const footer =
    '<p style="margin-top:32px;font-size:12px;color:#6b7280;text-align:center">[Your postal address, from CRM_EMAIL_POSTAL_ADDRESS]' +
    (hasUnsubscribeTag(bodyHtml) ? '' : '<br><a href="#unsubscribe" style="color:#6b7280">Unsubscribe</a>') +
    '</p>';
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><base target="_blank"><style>body{margin:0;padding:24px;font:15px/1.6 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;color:#1e293b;background:#fff;word-wrap:break-word}img{max-width:100%;height:auto}a{color:#4f46e5}</style></head><body>${body}${footer}</body></html>`;
};

/* ------------------------------------------------------------------ *
 * Ready to send?
 * ------------------------------------------------------------------ */

export type Missing = 'SUBJECT' | 'BODY' | 'AUDIENCE' | 'UNKNOWN_MERGE_FIELD' | 'FROM_EMAIL';

export const MISSING_LABEL: Record<string, string> = {
  SUBJECT: 'A subject',
  BODY: 'A body',
  FROM_EMAIL: 'A from address (or a default sender on the server)',
  AUDIENCE: 'Who it goes to: a list or saved filter',
  UNKNOWN_MERGE_FIELD: 'Only merge fields we know',
};

export interface DraftFields {
  name: string;
  subject: string;
  preheader: string;
  fromName: string;
  fromEmail: string;
  bodyHtml: string;
  segmentId: number | null;
  segmentName: string | null;
}

export const fieldsOf = (c: Campaign): DraftFields => ({
  name: c.name,
  subject: c.subject ?? '',
  preheader: c.preheader ?? '',
  fromName: c.fromName ?? '',
  fromEmail: c.fromEmail ?? '',
  bodyHtml: c.bodyHtml ?? '',
  segmentId: c.segmentId,
  segmentName: c.segmentName,
});

export const EMPTY_DRAFT: DraftFields = { name: '', subject: '', preheader: '', fromName: '', fromEmail: '', bodyHtml: '', segmentId: null, segmentName: null };

/**
 * What stops a campaign being scheduled, as the API checks it. The from address
 * is not here: blank means the server's default sender, which only it knows.
 */
export const draftProblems = (d: DraftFields, needsAudience = true): { missing: Array<Missing>; fields: Array<string> } => {
  const missing: Array<Missing> = [];
  if (!d.subject.trim()) missing.push('SUBJECT');
  if (!d.bodyHtml.trim()) missing.push('BODY');
  if (needsAudience && d.segmentId == null) missing.push('AUDIENCE');
  const fields = unknownMergeFields(d.subject, d.preheader, d.bodyHtml);
  if (fields.length) missing.push('UNKNOWN_MERGE_FIELD');
  return { missing, fields };
};

const EMAIL = /^[^\s@<>"]+@[^\s@<>"]+\.[^\s@<>"]+$/;
export const isEmail = (text: string) => EMAIL.test(text.trim()) && text.trim().length <= LIMITS.email;

/** Field problems the API would refuse on save, by field. */
export const fieldProblems = (d: DraftFields): Partial<Record<keyof DraftFields, string>> => {
  const out: Partial<Record<keyof DraftFields, string>> = {};
  if (!d.name.trim()) out.name = 'Give it a name (only admins see it).';
  if (d.fromEmail.trim() && !isEmail(d.fromEmail)) out.fromEmail = 'Not an email address.';
  return out;
};

/** Only the fields that differ from the saved campaign: what `crmUpdateEmailCampaign` gets. */
export const changedFields = (now: DraftFields, saved: DraftFields) => {
  const out: Record<string, unknown> = {};
  (['name', 'subject', 'preheader', 'fromName', 'fromEmail', 'bodyHtml'] as const).forEach((key) => {
    if (now[key] !== saved[key]) out[key] = key === 'name' ? now[key].trim() : now[key].trim() ? now[key] : null;
  });
  if (now.segmentId !== saved.segmentId) out.segmentId = now.segmentId;
  return out;
};

/** "a@x.org, b@y.org" or one a line: 1 to 5 addresses, each checked. */
export const parseTestRecipients = (text: string): { to: Array<string>; problem?: string } => {
  const to = text
    .split(/[\s,;]+/)
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean)
    .filter((t, i, all) => all.indexOf(t) === i);
  if (!to.length) return { to, problem: 'Give at least one address.' };
  if (to.length > LIMITS.testRecipients) return { to, problem: `At most ${LIMITS.testRecipients} addresses.` };
  const bad = to.find((t) => !isEmail(t));
  return bad ? { to, problem: `"${bad}" is not an email address.` } : { to };
};

/* ------------------------------------------------------------------ *
 * Refusals
 * ------------------------------------------------------------------ */

const firstError = (error: unknown) => (error instanceof ApolloError ? error.graphQLErrors?.[0] : undefined);
export const errorCode = (error: unknown) => firstError(error)?.extensions?.code as string | undefined;

export const changedCount = (error: unknown): number | undefined => {
  if (errorCode(error) !== 'CRM_SELECTION_CHANGED') return undefined;
  const n = firstError(error)?.extensions?.count;
  return typeof n === 'number' ? n : undefined;
};

export const fieldOfError = (error: unknown): string | undefined => {
  const field = firstError(error)?.extensions?.field;
  return typeof field === 'string' ? field : undefined;
};

export const describeEmailError = (error: unknown, fallback = 'That could not be done.'): string => {
  if (getRateLimitRejection(error)) return describeError(error, 'Too many at once');
  const first = firstError(error);
  const ext = first?.extensions ?? {};
  switch (errorCode(error)) {
    case 'CRM_SENDING_DISABLED':
      return 'Sending is switched off on this server, or its unsubscribe link or postal address is not set up. Nothing is wrong with the campaign.';
    case 'CRM_CAMPAIGN_INCOMPLETE': {
      const missing = Array.isArray(ext.missing) ? (ext.missing as Array<string>) : [];
      const fields = Array.isArray(ext.fields) ? (ext.fields as Array<string>) : [];
      const parts = missing.map((m) => (m === 'UNKNOWN_MERGE_FIELD' && fields.length ? `merge fields it cannot fill: ${fields.join(', ')}` : (MISSING_LABEL[m] ?? m).toLowerCase()));
      return parts.length ? `Not ready to send. It needs ${parts.join('; ')}.` : first?.message ?? fallback;
    }
    case 'CRM_SELECTION_CHANGED': {
      const n = changedCount(error);
      return n === undefined
        ? 'The number of people it reaches changed. Nothing was scheduled.'
        : `It can now be sent to ${count(n)} people, not the number you confirmed. Nothing was scheduled: check the new number and confirm again.`;
    }
    case 'CRM_EMAIL_SEND_FAILED':
      return 'SendGrid refused the test. Usually the from address is not a verified sender there.';
    case 'CRM_CAMPAIGN_STATE':
      return ext.reason === 'MIGRATED' ? 'This campaign came from NationBuilder and is kept as history. Duplicate it to send it again.' : first?.message ?? fallback;
    case 'CRM_IN_USE':
      return first?.message ?? 'A workflow sends this campaign, so it cannot also go as a blast. Duplicate it to send it on its own.';
    case 'CRM_CAMPAIGN_NOT_FOUND':
      return 'That campaign no longer exists. Someone may have just deleted it.';
    case 'CRM_LIST_NOT_FOUND':
      return 'That list no longer exists.';
    case 'CRM_INVALID_QUERY':
      return first?.message ?? fallback;
    case 'FORBIDDEN':
      return 'Only admins can send email.';
    default:
      if (error instanceof ApolloError && error.networkError) return 'Could not reach the API. Check before trying again.';
      return first?.message ?? fallback;
  }
};
